import { useState, useEffect } from "react";
import { motion } from "framer-motion";

function CalculatorPage({ result, setResult }) {
  const [consumptionType, setConsumptionType] = useState("Joint");
  const [thcPercentage, setThcPercentage] = useState(10);
  const [amount, setAmount] = useState(0.5);
  const [bodyWeight, setBodyWeight] = useState(70);
  const [consumptionTime, setConsumptionTime] = useState(2);
  const [calculationResult, setCalculationResult] = useState(null);
  
  const calculateTHC = () => {
    const bioavailability = {
      Joint: 0.3,
      Blunt: 0.3,
      Vaporizer: 0.5,
      Bong: 0.35,
      Edibles: 0.1,
    }[consumptionType];

    const K = 2;
    const V = 0.6;

    const thcBlut = Math.max(
      0,
      (amount * (thcPercentage / 100) * bioavailability * 1000) / (bodyWeight * V) -
        K * consumptionTime
    );

    let status = "Fahrtüchtigkeit unsicher";
    let color = "text-yellow-500";
    let percentage = 50;
    let ringColor = "from-yellow-300 to-yellow-500";

    if (thcBlut < 1) {
      status = "Fahrtüchtig";
      color = "text-green-500";
      percentage = 20;
      ringColor = "from-green-300 to-green-600";
    } else if (thcBlut < 3.5) {
      status = "Grenzwertig";
      color = "text-orange-500";
      percentage = 70;
      ringColor = "from-orange-300 to-orange-600";
    } else {
      status = "Nicht fahrtüchtig";
      color = "text-red-500";
      percentage = 100;
      ringColor = "from-red-300 to-red-600";
    }

    setCalculationResult({
      thcBlut: thcBlut.toFixed(2),
      status,
      color,
      percentage,
      ringColor
    });
    
    // Update parent state for persistence
    setResult(JSON.stringify({
      thcBlut: thcBlut.toFixed(2),
      status,
      color,
      percentage,
      ringColor
    }));
  };
  
  useEffect(() => {
    // Restore calculation if available
    if (result && typeof result === 'string' && result.length > 0) {
      try {
        setCalculationResult(JSON.parse(result));
      } catch (e) {
        console.error("Failed to parse saved result");
      }
    }
  }, [result]);

  return (
    <div className="min-h-screen p-4 pb-24 bg-gray-50 dark:bg-gray-900 flex flex-col items-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mt-8"
      >
        <h1 className="text-xl font-bold mb-6 text-gray-800 dark:text-white">THC Fahrtüchtigkeitsrechner</h1>
        
        <div className="space-y-6">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Konsumart</label>
            <select
              value={consumptionType}
              onChange={(e) => setConsumptionType(e.target.value)}
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
            >
              <option value="Joint">Joint</option>
              <option value="Blunt">Blunt</option>
              <option value="Vaporizer">Vaporizer</option>
              <option value="Bong">Bong</option>
              <option value="Edibles">Edibles</option>
            </select>
          </div>

          {/* THC-Gehalt Slider mit perfekt zentriertem Schiebeknopf */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">THC-Gehalt (%)</label>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{thcPercentage}%</span>
            </div>
            <div className="slider-container">
              <div className="slider-track"></div>
              <div className="slider-progress" style={{ width: `${(thcPercentage / 30) * 100}%` }}></div>
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={thcPercentage}
                onChange={(e) => setThcPercentage(Number(e.target.value))}
                className="relative w-full h-full appearance-none bg-transparent cursor-pointer z-10 slider-thumb"
              />
            </div>
          </div>

          {/* Menge Slider mit perfekt zentriertem Schiebeknopf */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Menge (g)</label>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{amount}g</span>
            </div>
            <div className="slider-container">
              <div className="slider-track"></div>
              <div className="slider-progress" style={{ width: `${(amount / 5) * 100}%` }}></div>
              <input
                type="range"
                min="0.1"
                max="5"
                step="0.1"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="relative w-full h-full appearance-none bg-transparent cursor-pointer z-10 slider-thumb"
              />
            </div>
          </div>

          {/* Körpergewicht-Feld mit verbesserter Eingabehandlung */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Körpergewicht (kg)</label>
            <input
              type="number"
              inputMode="decimal"
              value={bodyWeight}
              onChange={(e) => {
                // Erlaubt direkte Eingabe ohne sofortige Begrenzung
                const val = e.target.value;
                if (val === '' || val === null) {
                  setBodyWeight('');
                } else {
                  setBodyWeight(val);
                }
              }}
              onFocus={(e) => e.target.select()} // Selektiert den Text beim Fokussieren
              onBlur={(e) => {
                // Validierung erst nach Verlassen des Feldes
                const num = parseFloat(e.target.value);
                if (isNaN(num) || num < 30) {
                  setBodyWeight(30);
                } else if (num > 200) {
                  setBodyWeight(200);
                } else {
                  // Rundet auf 0.5-Schritte
                  setBodyWeight(Math.round(num * 2) / 2);
                }
              }}
              min="30"
              max="200"
              step="0.5"
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
            />
          </div>

          {/* Stunden-seit-Konsum-Feld mit verbesserter Eingabehandlung */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Stunden seit Konsum</label>
            <input
              type="number"
              inputMode="decimal"
              value={consumptionTime}
              onChange={(e) => {
                // Erlaubt direkte Eingabe ohne sofortige Begrenzung
                const val = e.target.value;
                if (val === '' || val === null) {
                  setConsumptionTime('');
                } else {
                  setConsumptionTime(val);
                }
              }}
              onFocus={(e) => e.target.select()} // Selektiert den Text beim Fokussieren
              onBlur={(e) => {
                // Validierung erst nach Verlassen des Feldes
                const num = parseFloat(e.target.value);
                if (isNaN(num) || num < 0) {
                  setConsumptionTime(0);
                } else if (num > 72) {
                  setConsumptionTime(72);
                } else {
                  // Rundet auf 0.25-Schritte
                  setConsumptionTime(Math.round(num * 4) / 4);
                }
              }}
              min="0"
              max="72"
              step="0.25"
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
            />
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={calculateTHC}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-200"
          >
            Berechnen
          </motion.button>
        </div>
      </motion.div>

      {calculationResult && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mt-4"
        >
          <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Ergebnis</h2>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">THC-Konzentration im Blut</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{calculationResult.thcBlut} <span className="text-sm font-normal">ng/ml</span></p>
            </div>
            
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="40" 
                    fill="none" 
                    stroke="rgba(200,200,200,0.2)" 
                    strokeWidth="8" 
                  />
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="40" 
                    fill="none" 
                    stroke={`url(#${calculationResult.color})`} 
                    strokeWidth="8" 
                    strokeDasharray="251.2" 
                    strokeDashoffset={251.2 - (251.2 * calculationResult.percentage / 100)}
                    transform="rotate(-90 50 50)" 
                  />
                  
                  <defs>
                    <linearGradient id={calculationResult.color} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={calculationResult.color.replace('text-', '').includes('green') ? '#86efac' : 
                                                  calculationResult.color.replace('text-', '').includes('yellow') ? '#fde047' : 
                                                  calculationResult.color.replace('text-', '').includes('orange') ? '#fdba74' : '#fca5a5'} />
                      <stop offset="100%" stopColor={calculationResult.color.replace('text-', '').includes('green') ? '#22c55e' : 
                                                   calculationResult.color.replace('text-', '').includes('yellow') ? '#eab308' :
                                                   calculationResult.color.replace('text-', '').includes('orange') ? '#f97316' : '#ef4444'} />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                  <span className={`font-bold text-sm ${calculationResult.color}`}>
                    {calculationResult.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-600 dark:text-gray-300">
            <p className="mb-1">
              <span className="font-medium">Unter 1 ng/ml:</span> In der Regel fahrtüchtig
            </p>
            <p className="mb-1">
              <span className="font-medium">1-3.5 ng/ml:</span> Grenzbereich, Fahrtüchtigkeit kann eingeschränkt sein
            </p>
            <p>
              <span className="font-medium">Über 3.5 ng/ml:</span> In der Regel nicht fahrtüchtig
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default CalculatorPage;